import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Store, ShoppingCart, Plus, Minus, Trash2, CreditCard, Loader2, X, Package, ChevronLeft, ChevronRight } from "lucide-react";

const euro = (n) => (n || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" });
const cartKey = (k, v) => `${k}::${v || ""}`;

export default function Boutique() {
  const [products, setProducts] = useState([]);
  const [cat, setCat] = useState("Tout");
  const [cart, setCart] = useState({}); // key -> {lookup_key, variant, qty, name, price}
  const [checking, setChecking] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailVariant, setDetailVariant] = useState("");
  const [detailImg, setDetailImg] = useState(0);

  useEffect(() => {
    api.get("/shop/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const categories = ["Tout", ...Array.from(new Set(products.map((p) => p.category)))];
  const shown = cat === "Tout" ? products : products.filter((p) => p.category === cat);

  const variantPrice = (p, vname) => {
    const v = (p.variants || []).find((x) => x.name === vname);
    return p.price + (v ? v.price_delta : 0);
  };

  const addToCart = (p, variant) => {
    const key = cartKey(p.lookup_key, variant);
    const price = variantPrice(p, variant);
    setCart((c) => ({ ...c, [key]: { lookup_key: p.lookup_key, variant: variant || "", name: p.name, price, qty: (c[key]?.qty || 0) + 1 } }));
    toast.success("Ajouté au panier");
  };
  const dec = (key) => setCart((c) => {
    const q = (c[key]?.qty || 0) - 1;
    const n = { ...c };
    if (q <= 0) delete n[key]; else n[key] = { ...n[key], qty: q };
    return n;
  });
  const inc = (key) => setCart((c) => ({ ...c, [key]: { ...c[key], qty: c[key].qty + 1 } }));
  const removeItem = (key) => setCart((c) => { const n = { ...c }; delete n[key]; return n; });

  const cartLines = Object.entries(cart).map(([key, l]) => ({ key, ...l }));
  const total = cartLines.reduce((s, l) => s + l.price * l.qty, 0);
  const count = cartLines.reduce((s, l) => s + l.qty, 0);

  const openDetail = (p) => { setDetail(p); setDetailVariant(p.variants?.[0]?.name || ""); setDetailImg(0); };

  const checkout = async () => {
    setChecking(true);
    try {
      const items = cartLines.map((l) => ({ lookup_key: l.lookup_key, variant: l.variant, quantity: l.qty }));
      const { data } = await api.post("/shop/checkout", { items, origin_url: window.location.origin });
      if (data.demo) { toast.info(data.message); setChecking(false); return; }
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Erreur lors du paiement");
      setChecking(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-navy flex items-center justify-center"><Store className="w-5 h-5 text-sky-400" /></div>
        <div>
          <h1 className="text-3xl font-display font-bold text-navy">Boutique & Fournitures</h1>
          <p className="text-slate-500">Outils, fournitures et matières premières — paiement CB sécurisé</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="shop-categories">
        {categories.map((c) => (
          <button key={c} onClick={() => setCat(c)} data-testid={`shop-cat-${c}`}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${cat === c ? "bg-navy text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-sky-300"}`}>
            {c}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 grid sm:grid-cols-2 gap-4">
          {shown.map((p) => (
            <div key={p.lookup_key} className="bg-white rounded-2xl border border-slate-200 overflow-hidden flex flex-col" data-testid={`product-${p.lookup_key}`}>
              <button onClick={() => openDetail(p)} className="h-40 bg-slate-100 overflow-hidden text-left relative" data-testid={`product-image-${p.lookup_key}`}>
                <img src={p.image} alt={p.name} className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" onError={(e) => (e.target.style.display = "none")} />
                {p.stock <= 10 && <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-amber-500 text-white text-[10px] font-bold">Stock : {p.stock}</span>}
              </button>
              <div className="p-4 flex flex-col flex-1">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-sky-600">{p.category}</span>
                <button onClick={() => openDetail(p)} className="font-semibold text-navy mt-1 leading-tight text-left hover:text-sky-600 transition-colors">{p.name}</button>
                <p className="text-xs text-slate-500 mt-1 flex-1 line-clamp-2">{p.desc}</p>
                {p.variants?.length > 1 && <p className="text-[11px] text-slate-400 mt-1">{p.variants.length} variantes</p>}
                <div className="flex items-center justify-between mt-3">
                  <span className="text-lg font-bold text-navy font-mono">{euro(p.price)}</span>
                  <button onClick={() => openDetail(p)} data-testid={`view-${p.lookup_key}`}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-sky-400 text-navy text-sm font-semibold hover:bg-sky-500 transition-colors">
                    <Package className="w-4 h-4" /> Voir la fiche
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5 h-fit sticky top-20" data-testid="cart-panel">
          <div className="flex items-center gap-2 font-display font-semibold text-navy">
            <ShoppingCart className="w-5 h-5 text-sky-500" /> Panier ({count})
          </div>
          <div className="mt-4 space-y-3">
            {cartLines.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">Votre panier est vide.</p>}
            {cartLines.map((l) => (
              <div key={l.key} className="flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-navy truncate">{l.name}</div>
                  {l.variant && <div className="text-[11px] text-slate-400 truncate">{l.variant}</div>}
                  <div className="text-xs text-slate-500 font-mono">{euro(l.price)} × {l.qty}</div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => dec(l.key)} className="p-1 rounded bg-slate-100 hover:bg-slate-200"><Minus className="w-3 h-3" /></button>
                  <span className="w-6 text-center text-sm font-mono">{l.qty}</span>
                  <button onClick={() => inc(l.key)} className="p-1 rounded bg-slate-100 hover:bg-slate-200"><Plus className="w-3 h-3" /></button>
                  <button onClick={() => removeItem(l.key)} className="p-1 rounded text-rose-500 hover:bg-rose-50"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            ))}
          </div>
          {cartLines.length > 0 && (
            <>
              <div className="flex justify-between mt-4 pt-4 border-t border-slate-100 font-bold text-navy">
                <span>Total</span><span className="font-mono" data-testid="cart-total">{euro(total)}</span>
              </div>
              <button onClick={checkout} disabled={checking} data-testid="checkout-btn"
                className="mt-4 w-full py-3 rounded-xl bg-navy text-white font-semibold hover:bg-navy-deep transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60">
                {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                Payer par carte
              </button>
              <p className="text-[11px] text-slate-400 text-center mt-2">Test : carte 4242 4242 4242 4242</p>
            </>
          )}
        </div>
      </div>

      {/* Product detail modal */}
      {detail && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setDetail(null)}>
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="product-detail">
            <div className="flex items-center justify-between p-4 border-b border-slate-100 sticky top-0 bg-white z-10">
              <h2 className="text-lg font-display font-bold text-navy truncate">{detail.name}</h2>
              <button onClick={() => setDetail(null)} className="p-1.5 rounded-lg hover:bg-slate-100"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid md:grid-cols-2 gap-6 p-6">
              <div>
                <div className="relative h-64 rounded-2xl bg-slate-100 overflow-hidden">
                  <img src={(detail.images || [detail.image])[detailImg]} alt={detail.name} className="w-full h-full object-cover" data-testid="detail-main-image" onError={(e) => (e.target.style.display = "none")} />
                  {(detail.images || []).length > 1 && (
                    <>
                      <button onClick={() => setDetailImg((i) => (i - 1 + detail.images.length) % detail.images.length)} className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-white/80 hover:bg-white shadow"><ChevronLeft className="w-4 h-4" /></button>
                      <button onClick={() => setDetailImg((i) => (i + 1) % detail.images.length)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-white/80 hover:bg-white shadow"><ChevronRight className="w-4 h-4" /></button>
                    </>
                  )}
                </div>
                {(detail.images || []).length > 1 && (
                  <div className="flex gap-2 mt-3">
                    {detail.images.map((im, i) => (
                      <button key={i} onClick={() => setDetailImg(i)} data-testid={`thumb-${i}`}
                        className={`w-16 h-16 rounded-xl overflow-hidden border-2 ${detailImg === i ? "border-sky-400" : "border-transparent"}`}>
                        <img src={im} alt="" className="w-full h-full object-cover" onError={(e) => (e.target.style.display = "none")} />
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex flex-col">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-sky-600">{detail.category}</span>
                <div className="text-2xl font-bold text-navy font-mono mt-1">{euro(variantPrice(detail, detailVariant))}</div>
                <div className={`text-xs font-medium mt-1 ${detail.stock <= 10 ? "text-amber-600" : "text-emerald-600"}`}>{detail.stock > 0 ? `En stock : ${detail.stock}` : "Rupture"}</div>
                <p className="text-sm text-slate-600 mt-3 leading-relaxed flex-1">{detail.long_description || detail.desc}</p>

                {detail.variants?.length > 0 && (
                  <div className="mt-4">
                    <label className="text-xs font-medium text-slate-500">Variante</label>
                    <div className="flex flex-wrap gap-2 mt-1.5" data-testid="variant-picker">
                      {detail.variants.map((v) => (
                        <button key={v.name} onClick={() => setDetailVariant(v.name)} data-testid={`variant-${v.name}`}
                          className={`px-3 py-1.5 rounded-xl text-sm font-medium border transition-colors ${detailVariant === v.name ? "bg-navy text-white border-navy" : "bg-white border-slate-200 text-slate-600 hover:border-sky-300"}`}>
                          {v.name}{v.price_delta ? ` (+${euro(v.price_delta)})` : ""}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <button onClick={() => { addToCart(detail, detailVariant); setDetail(null); }} data-testid="detail-add-cart"
                  className="mt-5 w-full py-3 rounded-xl bg-sky-400 text-navy font-semibold hover:bg-sky-500 transition-colors inline-flex items-center justify-center gap-2">
                  <Plus className="w-4 h-4" /> Ajouter au panier
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
